import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ROLE_LABELS } from "@/lib/api";
import { DemoBadge } from "@/components/DemoBadge";
import { BrandLogo } from "@/lib/brand";
import { NotificationBell } from "@/components/NotificationBell";
import {
  LayoutDashboard, Building2, ClipboardCheck, Users, MapPinned,
  BarChart3, Activity, ListTodo, Images, Upload, FileBarChart2,
  UserCog, Settings2, Menu, X, LogOut, Map, GraduationCap, FileText, TrendingUp, ShieldCheck, Target,
  Package, Lightbulb, LayoutGrid, PieChart, Truck,
} from "lucide-react";

const NAV = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/sales-dashboard", icon: PieChart, label: "Sales Dashboard" },
  { to: "/accounts", icon: Building2, label: "Account Performance" },
  { to: "/product-coverage", icon: LayoutGrid, label: "Product Coverage" },
  { to: "/coverage-dashboard", icon: LayoutGrid, label: "Coverage Dashboard" },
  { to: "/sales-opportunities", icon: Lightbulb, label: "Sales Opportunities" },
  { to: "/floor-display-report", icon: LayoutGrid, label: "Floor Display Report" },
  { to: "/pos-requests", icon: Truck, label: "POS Requests" },
  { to: "/pos-catalogue", icon: Package, label: "POS Materials", roles: ["super_admin", "management", "general_manager"] },
  { to: "/pos-report", icon: Truck, label: "POS Report", roles: ["super_admin", "management", "general_manager"] },
  { to: "/customers", icon: Building2, label: "Customers & Stores" },
  { to: "/visits", icon: ClipboardCheck, label: "Store Visits" },
  { to: "/training", icon: GraduationCap, label: "Training Reports" },
  { to: "/training/impact", icon: TrendingUp, label: "Training Impact" },
  { to: "/docs", icon: FileText, label: "Training Documents" },
  { to: "/agents", icon: Users, label: "Agents & Territories" },
  { to: "/trips", icon: MapPinned, label: "Trips" },
  { to: "/map", icon: Map, label: "Territory Map" },
  { to: "/sales", icon: BarChart3, label: "Sales Performance" },
  { to: "/targets", icon: Target, label: "Sales Targets" },
  { to: "/products", icon: Package, label: "Product Catalogue", roles: ["super_admin", "management", "general_manager"] },
  { to: "/activity", icon: Activity, label: "Customer Activity" },
  { to: "/tasks", icon: ListTodo, label: "Tasks & Follow-ups" },
  { to: "/photos", icon: Images, label: "Photo Library" },
  { to: "/import", icon: Upload, label: "Sales Import", roles: ["super_admin", "management", "general_manager"] },
  { to: "/reports", icon: FileBarChart2, label: "Reports" },
  { to: "/users", icon: UserCog, label: "User Management", roles: ["super_admin"] },
  { to: "/activity-log", icon: ShieldCheck, label: "Activity Log", roles: ["super_admin"] },
  { to: "/settings", icon: Settings2, label: "Settings", roles: ["super_admin", "management", "general_manager"] },
];

const Sidebar = ({ user, onLogout, close }) => (
  <aside className="w-64 shrink-0 h-full bg-[#001F54] text-white flex flex-col">
    <div className="px-5 py-5 border-b border-white/10 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <BrandLogo className="w-10 h-10 rounded-lg bg-white/10 object-contain shrink-0" />
        <div>
          <div className="text-[10px] tracking-[0.3em] text-white/50 font-semibold">MLILY</div>
          <div className="text-xl font-heading font-bold tracking-tight">CRM</div>
        </div>
      </div>
      {close && (
        <button className="lg:hidden text-white/70 hover:text-white" onClick={close} data-testid="close-menu-btn">
          <X className="w-5 h-5" />
        </button>
      )}
    </div>
    <nav className="flex-1 overflow-y-auto p-3 space-y-1">
      {NAV.filter(n => !n.roles || n.roles.includes(user.role)).map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.to === "/"}
          onClick={close}
          className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
          data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
        >
          <n.icon className="w-4 h-4" />
          <span>{n.label}</span>
        </NavLink>
      ))}
    </nav>
    <div className="p-3 border-t border-white/10">
      <div className="px-3 py-2 text-xs text-white/60">Signed in as</div>
      <div className="px-3 pb-1 text-sm font-medium">{user.name}</div>
      <div className="px-3 pb-2 text-xs text-white/50 flex items-center gap-2">
        <span>{ROLE_LABELS[user.role]}</span>
        <DemoBadge />
      </div>
      <button
        onClick={onLogout}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md bg-white/5 hover:bg-white/10 transition"
        data-testid="logout-btn"
      >
        <LogOut className="w-4 h-4" /> Log out
      </button>
    </div>
  </aside>
);

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  if (!user) return null;

  const doLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen flex bg-[#F3F7FC]">
      {/* Desktop Sidebar */}
      <div className="hidden lg:block">
        <Sidebar user={user} onLogout={doLogout} />
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-64">
            <Sidebar user={user} onLogout={doLogout} close={() => setOpen(false)} />
          </div>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Mobile top bar */}
        <header className="lg:hidden sticky top-0 z-30 bg-[#001F54] text-white px-4 py-3 flex items-center justify-between">
          <button onClick={() => setOpen(true)} data-testid="open-menu-btn">
            <Menu className="w-6 h-6" />
          </button>
          <div className="font-black font-heading">MLILY <span className="text-[#003DA5]">CRM</span></div>
          <NotificationBell />
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1600px] w-full mx-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
